
import { Component, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { DateTime } from 'luxon';
import { capitalize } from 'lodash';
import CalendarNavigator from '@/components/menu/rotational-menus/CalendarNavigator.vue';

const namespace: string = 'restaurant';

@Component<MonthNavigator>({
	components: {
		CalendarNavigator
	}
})
export default class MonthNavigator extends Vue {
	@Action('setCalendarMonth', { namespace }) private setCalendarMonth!: (anchorISO: string) => Promise<void>;
	@Getter('getDateRange', { namespace }) private dateRange!: DateRange;
	@Getter('getZone', { namespace }) private zone!: string;
	@Getter('getOrderCutoffDate', { namespace }) private orderCutoffDate!: DateTime | null;
	@Getter('getMaxOrderDate', { namespace }) private maxOrderDate!: DateTime | undefined;

	/**
	 * Localized month label for the navigator (e.g. "May 2026").
	 *
	 * @return {string}
	 */
	private get monthLabel(): string {
		if (!this.dateRange.start) {
			return '';
		}
		return capitalize(DateTime.fromISO(this.dateRange.start).setLocale(this.$i18n.locale).toFormat('LLLL yyyy'));
	}

	/**
	 * Disable the previous arrow when the displayed month is at or before the
	 * earliest orderable month (order cutoff, or the current month if none).
	 *
	 * @return {boolean}
	 */
	private get isPrevDisabled(): boolean {
		if (!this.dateRange.start) {
			return false;
		}
		// Parse in the restaurant zone so the comparison matches the zoned cutoff/now
		// (a bare fromISO uses the browser zone, which shifts the month boundary for
		// out-of-zone users and wrongly enables the previous arrow).
		const startOfMonth = DateTime.fromISO(this.dateRange.start, { zone: this.zone }).startOf('month');
		if (this.orderCutoffDate) {
			return startOfMonth <= this.orderCutoffDate.startOf('month');
		}
		return startOfMonth <= DateTime.now().setZone(this.zone).startOf('month');
	}

	/**
	 * Disable the next arrow when the following month is past the advanced-order limit.
	 *
	 * @return {boolean}
	 */
	private get isNextDisabled(): boolean {
		if (!this.dateRange.start || !this.maxOrderDate) {
			return false;
		}
		return DateTime.fromISO(this.dateRange.start, { zone: this.zone }).plus({ months: 1 }).startOf('month') > this.maxOrderDate;
	}

	/**
	 * Move to the previous or next month and fetch its menus.
	 *
	 * @param {'prev' | 'next'} direction
	 * @return {Promise<void>}
	 */
	private async changeMonth(direction: 'prev' | 'next'): Promise<void> {
		if (!this.dateRange.start) {
			return;
		}
		const monthISO = DateTime.fromISO(this.dateRange.start).plus({ months: direction === 'prev' ? -1 : 1 }).toISODate()!;
		await this.setCalendarMonth(monthISO);
		this.$router.replace({ query: { ...this.$route.query, selectedMonth: monthISO } }).catch(() => {});
	}
}
